import React, {useEffect, useState} from "react";
import "./anniv2026.css";
import "./reveal2026.css";
import {Login2026} from "./Login2026";
import {errorMessage, post} from "./api2026";
import {CategoryIcon} from "./PodiumModal2026";
import {Fireworks2026} from "./Fireworks2026";

// steps of a category, one click each
const SHOW_CATEGORY = 0;
const SHOW_THIRD = 1;
const SHOW_SECOND = 2;
const SHOW_FIRST = 3;

const OTHERS_DELAY = 1800;
const OTHERS_INTERVAL = 400;

// last category of the reveal only: the points of every category added up
function bestGameCategory(categories) {
    const totals = {};
    categories.forEach(category => category.ranking.forEach(game => {
        if (!totals[game.id]) {
            totals[game.id] = {...game, points: 0, ranks: [0, 0, 0]};
        }
        totals[game.id].points += game.points;
        game.ranks.forEach((count, index) => totals[game.id].ranks[index] += count);
    }));
    const ranking = Object.values(totals).sort((a, b) =>
        b.points - a.points || b.ranks[0] - a.ranks[0] || b.ranks[1] - a.ranks[1] || a.name.localeCompare(b.name));
    return {id: "meilleur", label: "Meilleur jeu", gold: true, ranking};
}

/*
 * Results reveal, meant to be projected on a big screen.
 * Entirely driven by the front: a click (or space / right arrow) goes to the next step, a refresh starts over.
 */
export const RevealAnniv2026Page = () => {

    const [player, setPlayer] = useState(undefined);
    const [results, setResults] = useState(undefined);
    const [error, setError] = useState("");
    // -1: intro screen, categories.length: end screen
    const [categoryIndex, setCategoryIndex] = useState(-1);
    const [phase, setPhase] = useState(SHOW_CATEGORY);
    const [othersShown, setOthersShown] = useState(0);
    // the winner shows up when the first firework explodes
    const [winnerRevealed, setWinnerRevealed] = useState(false);

    useEffect(() => {
        if (player && player.admin) {
            Promise.all([post("/admin/results"), post("/admin/games")]).then(([data, gamesData]) => {
                // games without any vote are not in the results, they are added at the end with 0 points
                const games = gamesData.games.filter(game => !game.hidden).sort((a, b) => a.name.localeCompare(b.name));
                data.results.forEach(category => {
                    const ranked = new Set(category.ranking.map(game => game.id));
                    games.filter(game => !ranked.has(game.id))
                        .forEach(game => category.ranking.push({...game, points: 0, ranks: [0, 0, 0]}));
                });
                data.results.push(bestGameCategory(data.results));
                setResults(data);
            }).catch(err => setError(errorMessage(err)));
        }
    }, [player]);

    const categories = results ? results.results : [];
    const category = categories[categoryIndex];
    const ranking = category ? category.ranking : [];
    const others = ranking.slice(3);

    function next() {
        if (!results || categoryIndex >= categories.length) {
            return;
        }
        if (phase === SHOW_FIRST && !winnerRevealed) {
            return;
        }
        if (categoryIndex === -1 || phase === SHOW_FIRST) {
            setCategoryIndex(categoryIndex + 1);
            setPhase(SHOW_CATEGORY);
            setOthersShown(0);
            setWinnerRevealed(false);
        } else {
            setPhase(phase + 1);
        }
    }

    useEffect(() => {
        function onKey(e) {
            if ([" ", "ArrowRight", "Enter", "PageDown"].includes(e.key)) {
                e.preventDefault();
                next();
            }
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    });

    // safety net if the fireworks never explode
    useEffect(() => {
        if (phase === SHOW_FIRST && !winnerRevealed) {
            const timer = setTimeout(() => setWinnerRevealed(true), 4000);
            return () => clearTimeout(timer);
        }
    }, [phase, winnerRevealed]);

    // once the winner is revealed, the next ones appear one by one
    useEffect(() => {
        if (!winnerRevealed || othersShown >= others.length) {
            return;
        }
        const timer = setTimeout(() => setOthersShown(othersShown + 1), othersShown === 0 ? OTHERS_DELAY : OTHERS_INTERVAL);
        return () => clearTimeout(timer);
    }, [winnerRevealed, othersShown, others.length]);

    if (!player || !player.admin || !results) {
        return (
            <div className="anniv2026 anniv2026-reveal-login">
                {!player ? <Login2026 setPlayer={setPlayer}/> : undefined}
                {player && !player.admin ? <div>Page réservée à l'admin.</div> : undefined}
                {player && player.admin && !results ? <div>{error || "Chargement des résultats..."}</div> : undefined}
            </div>
        );
    }

    function renderPodiumStep(rank, revealed) {
        const game = ranking[rank];
        return (
            <div className={"reveal-podium-column rank" + rank}>
                <div className={"reveal-podium-game" + (revealed ? " revealed" : "")}>
                    {
                        game ?
                            <>
                                <img src={game.logo} alt=""/>
                                <div className="reveal-game-name">{game.name}</div>
                                <div className="reveal-game-author">{game.authorName}</div>
                                <div className="reveal-game-points">{game.points} pts</div>
                            </> :
                            <div className="reveal-game-name">—</div>
                    }
                </div>
                <div className="reveal-podium-step">
                    <span>{rank + 1}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="anniv2026 anniv2026-reveal" onClick={next}>
            <img className="reveal-logo" src="https://nostragames.ai/logo.png" alt="Nostra Games"/>
            {
                categoryIndex === -1 ?
                    <div className="reveal-intro">
                        <img src="https://nostragames.ai/logo.png" alt="Nostra Games"/>
                        <div className="reveal-intro-title">Concours 2026</div>
                        <div className="reveal-intro-subtitle">Les résultats</div>
                        <div className="reveal-intro-hint">{results.voters} votants · {categories.length} catégories</div>
                    </div> :
                    categoryIndex >= categories.length ?
                        <div className="reveal-intro">
                            <div className="reveal-intro-title">Merci à tous !</div>
                            <div className="reveal-intro-subtitle">Bravo aux auteurs</div>
                        </div> :
                        <div className="reveal-category-screen" key={category.id}>
                            <div className="reveal-top">
                                <div className={"reveal-category" + (category.gold ? " gold" : "")}>
                                    <div className="reveal-category-counter">{categoryIndex + 1} / {categories.length}</div>
                                    <CategoryIcon category={category}/>
                                    <div className="reveal-category-label">{category.label}</div>
                                </div>
                                <div className="reveal-podium">
                                    {renderPodiumStep(1, phase >= SHOW_SECOND)}
                                    {renderPodiumStep(0, winnerRevealed)}
                                    {renderPodiumStep(2, phase >= SHOW_THIRD)}
                                </div>
                            </div>
                            {phase === SHOW_FIRST ? <Fireworks2026 onFirstExplosion={() => setWinnerRevealed(true)}/> : undefined}
                            <div className="reveal-others">
                                {
                                    others.slice(0, othersShown).map((game, index) =>
                                        <div key={game.id} className="reveal-other">
                                            <div className="reveal-other-rank">{index + 4}</div>
                                            <img src={game.logo} alt=""/>
                                            <div className="reveal-other-text">
                                                <div className="reveal-other-name">{game.name}</div>
                                                <div className="reveal-other-points">{game.points} pts</div>
                                            </div>
                                        </div>
                                    )
                                }
                            </div>
                        </div>
            }
        </div>
    );
}
